import { adminService } from '@/api/services';
import type { IApiSuccessMessageResponse, UnwrapData } from '@/api/types';
import type { EParticipantsStatus } from '@/shared/consts';

import type {
  IParticipantsImportsResponse,
  IParticipantsImportResponse,
  IParticipantsResponse,
  ICreateParticipantsImportResponse,
  INewParticipantsImportResponse,
} from './types';

export const schoolParticipantsService = adminService.injectEndpoints({
  endpoints: (builder) => ({
    /**
     * Получить список участников школы
     */
    getSchoolParticipants: builder.query<
      IParticipantsResponse,
      {
        schoolId: number;
        page?: number;
        perPage?: number;
        statusFilter?: EParticipantsStatus[] | null;
        nameOrEmailSearchString?: string;
      }
    >({
      query: ({ schoolId, page, perPage, statusFilter, nameOrEmailSearchString }) => ({
        method: 'GET',
        url: `/schools/${schoolId}/school_memberships`,
        params: {
          page,
          per_page: perPage,
          q: {
            user_full_name_or_email_cont: nameOrEmailSearchString,
            status_in: statusFilter,
          },
        },
      }),
      providesTags: (_, __, { schoolId }) => [{ type: 'SchoolParticipants', id: schoolId }],
    }),
    /**
     * Отправить приглашения в корпоративную школу
     */
    sendSchoolParticipantsInvites: builder.mutation<
      IApiSuccessMessageResponse,
      { schoolId: number; invitationLink: string }
    >({
      query: ({ invitationLink, schoolId }) => ({
        method: 'POST',
        url: `/schools/${schoolId}/send_invitations`,
        body: {
          data: { invitation_link: invitationLink },
        },
      }),
      invalidatesTags: (_, __, { schoolId }) => [{ type: 'SchoolParticipants', id: schoolId }],
    }),
    /**
     * Получить список импортов участников корпоративной школы
     */
    getSchoolParticipantsImportsList: builder.query<IParticipantsImportsResponse, { schoolId: number }>({
      query: ({ schoolId }) => ({
        method: 'GET',
        url: `/schools/${schoolId}/bulk_imports`,
      }),
      providesTags: (_, __, { schoolId }) => [{ type: 'SchoolParticipantsImports', id: schoolId }],
    }),
    /**
     * Получить данные по конкретному импорту участников корпоративной школы
     */
    getSchoolParticipantsImport: builder.query<
      UnwrapData<IParticipantsImportResponse>,
      { schoolId: number; bulkImportId: number }
    >({
      query: ({ schoolId, bulkImportId }) => ({
        method: 'GET',
        url: `/schools/${schoolId}/bulk_imports/${bulkImportId}`,
      }),
      transformResponse: (response: IParticipantsImportResponse): UnwrapData<IParticipantsImportResponse> =>
        response.data,
    }),
    /**
     * Отправить файл со списком участников корпоративной школы для загрузки
     */
    sendSchoolParticipantsImportsLists: builder.mutation<
      UnwrapData<ICreateParticipantsImportResponse>,
      { schoolId: number; file: string }
    >({
      query: ({ file, schoolId }) => ({
        method: 'POST',
        url: `/schools/${schoolId}/bulk_imports`,
        body: {
          data: { file },
        },
      }),
      invalidatesTags: (_, __, { schoolId }) => [{ type: 'SchoolParticipantsImports', id: schoolId }],
      transformResponse: (response: ICreateParticipantsImportResponse): UnwrapData<ICreateParticipantsImportResponse> =>
        response.data,
    }),
    /**
     * Загрузить новых пользователей в школу минуя опрос
     */
    uploadNewParticipants: builder.mutation<
      UnwrapData<INewParticipantsImportResponse>,
      { schoolId: number; file: string }
    >({
      query: ({ file, schoolId }) => ({
        method: 'POST',
        url: `/schools/${schoolId}/school_memberships/bulk_enroll`,
        body: {
          data: { file },
        },
      }),
      invalidatesTags: (_, __, { schoolId }) => [{ type: 'SchoolParticipants', id: schoolId }],
      transformResponse: (response: INewParticipantsImportResponse) => response.data,
    }),
  }),
});

export const {
  useGetSchoolParticipantsQuery,
  useLazyGetSchoolParticipantsQuery,
  useSendSchoolParticipantsInvitesMutation,
  useGetSchoolParticipantsImportsListQuery,
  useLazyGetSchoolParticipantsImportsListQuery,
  useSendSchoolParticipantsImportsListsMutation,
  useLazyGetSchoolParticipantsImportQuery,
  useUploadNewParticipantsMutation,
} = schoolParticipantsService;
